import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { colors } from "@/theme/tokens";
import { useAuth } from "@/hooks/useAuth";
import { useUIStore } from "@/store/uiStore";
import { signOut } from "@/features/auth/authService";

const NAV_LINKS = [
  { path: "/",         label: "Browse"   },
  { path: "/my-shelf", label: "My Shelf" },
  { path: "/requests", label: "Requests" },
];

export const Navbar: React.FC = () => {
  const { user, isSignedIn, displayName } = useAuth();
  const { showModal } = useUIStore();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [menuOpen, setMenuOpen]   = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const initials = displayName
    ? displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "AB";

  return (
    <>
      <header className="navbar-header">
        <div className="navbar-inner">

          {/* ── Logo ── */}
          <Link to="/" className="navbar-logo">
            <div className="navbar-logo-icon">
              📚
              <div className="navbar-logo-dot" />
            </div>
            <span className="navbar-logo-text">
              <span style={{ color: colors.dark }}>Adhvi</span>
              <span style={{ color: colors.accent }}>Books</span>
            </span>
          </Link>

          {/* ── Desktop nav ── */}
          <nav className="desktop-nav navbar-links">
            {NAV_LINKS.map(({ path, label }) => (
              <Link key={path} to={path} style={{ textDecoration: "none" }}>
                <span className={`navbar-link${isActive(path) ? " navbar-link--active" : ""}`}>
                  {label}
                </span>
              </Link>
            ))}
          </nav>

          {/* ── Actions ── */}
          <div className="navbar-actions">
            {isSignedIn ? (
              <>
                <Link to="/add-book" className="desktop-nav" style={{ display: "none", textDecoration: "none" }}>
                  <button className="btn-share">
                    <span className="btn-share-icon">+</span>
                    <span>Share a book</span>
                  </button>
                </Link>

                {/* Avatar / dropdown */}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="navbar-avatar"
                    aria-label="User menu"
                    aria-expanded={menuOpen}
                  >
                    {user?.photoURL
                      ? <img src={user.photoURL} alt={displayName ?? ""} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                      : initials
                    }
                  </button>

                  {menuOpen && (
                    <div className="navbar-dropdown" onMouseLeave={() => setMenuOpen(false)}>
                      {[
                        { label: "My Shelf",    path: "/my-shelf", icon: "📚" },
                        { label: "My Requests", path: "/requests", icon: "🔔" },
                        { label: "Settings",    path: "/settings", icon: "⚙️" },
                      ].map(item => (
                        <Link key={item.path} to={item.path} style={{ textDecoration: "none" }}>
                          <div className="dropdown-item" onClick={() => setMenuOpen(false)}>
                            <span>{item.icon}</span>{item.label}
                          </div>
                        </Link>
                      ))}
                      <div className="dropdown-divider" />
                      <div
                        className="dropdown-item dropdown-item--danger"
                        onClick={async () => { setMenuOpen(false); await signOut(); navigate("/"); }}
                      >
                        <span>🚪</span> Sign out
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button className="btn-signin" onClick={() => showModal("signIn")}>
                  Sign in
                </button>
                <button className="btn-share" onClick={() => showModal("signIn")}>
                  <span className="btn-share-icon">+</span>
                  <span className="btn-share-label">Share a book</span>
                </button>
              </>
            )}

            {/* Hamburger — shown on mobile via CSS */}
            <button
              className="hamburger-btn"
              onClick={() => setMobileOpen(v => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen
                ? <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                : <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M2 5h16M2 10h16M2 15h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              }
            </button>
          </div>
        </div>

        {/* ── Mobile menu ── */}
        {mobileOpen && (
          <div className="mobile-menu">
            {NAV_LINKS.map(({ path, label }) => (
              <Link key={path} to={path} style={{ textDecoration: "none" }}>
                <div className={`mobile-menu-link${isActive(path) ? " mobile-menu-link--active" : ""}`}>
                  {label}
                </div>
              </Link>
            ))}
            <div className="mobile-menu-divider" />
            {!isSignedIn && (
              <button className="btn-signin mobile-signin-btn" onClick={() => { setMobileOpen(false); showModal("signIn"); }}>
                Sign in
              </button>
            )}
            <button
              className="btn-share mobile-share-btn"
              onClick={() => { setMobileOpen(false); isSignedIn ? navigate("/add-book") : showModal("signIn"); }}
            >
              <span>+</span> Share a book with your community
            </button>
          </div>
        )}
      </header>

      <style>{`
        /* ── Header shell ── */
        .navbar-header {
          position: sticky;
          top: 0;
          z-index: 100;
          background: #fff;
          border-bottom: 1px solid ${colors.border};
          box-shadow: 0 2px 16px rgba(83,74,183,.07);
        }
        .navbar-inner {
          max-width: 1440px;
          margin: 0 auto;
          height: 64px;
          padding: 0 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        /* ── Logo ── */
        .navbar-logo {
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .navbar-logo-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, ${colors.primary}, #7C6FE0);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          position: relative;
          box-shadow: 0 4px 12px rgba(83,74,183,.32);
          flex-shrink: 0;
        }
        .navbar-logo-dot {
          position: absolute;
          width: 10px;
          height: 10px;
          background: ${colors.accent};
          border-radius: 50%;
          bottom: -2px;
          right: -2px;
          border: 2px solid #fff;
        }
        .navbar-logo-text {
          font-family: var(--font-display);
          font-weight: 800;
          font-size: 20px;
          letter-spacing: -0.5px;
        }

        /* ── Nav links ── */
        .navbar-links {
          flex: 1;
          justify-content: center !important;
          gap: 2px;
        }
        .navbar-link {
          display: block;
          padding: 7px 16px;
          border-radius: 9px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          color: ${colors.text};
          transition: all .15s;
          white-space: nowrap;
        }
        .navbar-link:hover { background: ${colors.surfaceAlt}; color: ${colors.primary}; }
        .navbar-link--active { background: ${colors.surfaceAlt}; color: ${colors.primary}; }

        /* ── Actions area ── */
        .navbar-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        /* ── Sign in button ── */
        .btn-signin {
          background: transparent;
          border: 1.5px solid ${colors.borderMid};
          border-radius: 10px;
          padding: 8px 18px;
          font-size: 13px;
          font-weight: 700;
          color: ${colors.primary};
          cursor: pointer;
          transition: all .15s;
          white-space: nowrap;
        }
        .btn-signin:hover { border-color: ${colors.primary}; background: ${colors.surfaceAlt}; }

        /* ── Share button ── */
        .btn-share {
          background: linear-gradient(135deg, ${colors.primary} 0%, #7C6FE0 100%);
          border: none;
          border-radius: 10px;
          padding: 9px 18px;
          font-size: 13px;
          font-weight: 800;
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 7px;
          font-family: var(--font-display);
          box-shadow: 0 4px 14px rgba(83,74,183,.38);
          transition: all .18s;
          white-space: nowrap;
        }
        .btn-share:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(83,74,183,.48); }
        .btn-share:active { transform: translateY(0); }
        .btn-share-icon { font-size: 16px; font-weight: 900; }

        /* ── Avatar ── */
        .navbar-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, ${colors.primary}, #7C6FE0);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
          color: #fff;
          font-family: var(--font-display);
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(83,74,183,.28);
          transition: box-shadow .15s;
        }
        .navbar-avatar:hover { box-shadow: 0 4px 14px rgba(83,74,183,.42); }

        /* ── Avatar dropdown ── */
        .navbar-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + 10px);
          background: #fff;
          border: 1px solid ${colors.border};
          border-radius: 14px;
          padding: 6px;
          min-width: 175px;
          box-shadow: 0 8px 32px rgba(83,74,183,.16);
          z-index: 200;
        }
        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 13px;
          border-radius: 9px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          color: ${colors.text};
          transition: background .12s;
        }
        .dropdown-item:hover { background: ${colors.surfaceAlt}; }
        .dropdown-item--danger { color: ${colors.error}; }
        .dropdown-divider { border-top: 1px solid ${colors.border}; margin: 4px 0; }

        /* ── Hamburger ── */
        .hamburger-btn {
          display: none;
          background: none;
          border: 1.5px solid ${colors.border};
          border-radius: 9px;
          padding: 7px 9px;
          cursor: pointer;
          color: ${colors.dark};
          align-items: center;
          justify-content: center;
          transition: border-color .15s;
        }
        .hamburger-btn:hover { border-color: ${colors.borderMid}; }

        /* ── Mobile menu ── */
        .mobile-menu {
          background: #fff;
          border-top: 1px solid ${colors.border};
          padding: 10px 16px 16px;
          box-shadow: 0 8px 24px rgba(83,74,183,.09);
        }
        .mobile-menu-link {
          padding: 11px 14px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
          color: ${colors.text};
          cursor: pointer;
          margin-bottom: 2px;
        }
        .mobile-menu-link--active { color: ${colors.primary}; background: ${colors.surfaceAlt}; }
        .mobile-menu-divider { border-top: 1px solid ${colors.border}; margin: 10px 0; }
        .mobile-signin-btn {
          display: block;
          width: 100%;
          margin-bottom: 8px;
          padding: 11px 18px;
          font-size: 14px;
          border-radius: 10px;
        }
        .mobile-share-btn {
          width: 100%;
          justify-content: center;
          padding: 13px 18px;
          font-size: 14px;
          border-radius: 12px;
          box-shadow: 0 3px 14px rgba(83,74,183,.32);
        }

        /* ── Responsive breakpoints ── */

        /* Sign in: hidden on mobile (bottom nav / hamburger handles it), visible 768px+ */
        .btn-signin { display: none; }
        @media (min-width: 768px) {
          .btn-signin { display: flex !important; }
        }

        /* Hamburger: show below 1024px, hide at 1024px+ (where desktop nav takes over) */
        @media (max-width: 1023px) {
          .hamburger-btn { display: flex !important; }
          .btn-share-label { display: none; }
          .btn-share { padding: 9px 11px !important; gap: 0 !important; box-shadow: none !important; }
          .navbar-logo-text { font-size: 18px; }
        }
        @media (min-width: 1024px) {
          .hamburger-btn { display: none !important; }
          .mobile-menu   { display: none !important; }
          .btn-share-label { display: inline; }
          .btn-share { padding: 9px 18px !important; gap: 7px !important; box-shadow: 0 4px 14px rgba(83,74,183,.38) !important; }
        }

        /* Tablet: 768–1023px — sign in + share + hamburger, no desktop nav */
        @media (min-width: 768px) and (max-width: 1023px) {
          .btn-signin { padding: 7px 14px !important; font-size: 12px !important; }
          .btn-share-label { display: none; }
        }
      `}</style>
    </>
  );
};
